import React, { useMemo, useImperativeHandle, useEffect, useState } from 'react';
import { Button, Form, message, Space, Table } from 'antd';
import { useMutation } from '@apollo/client';
import { DeleteOutlined } from '@ant-design/icons';
import { flags } from '../../featureFlags/flags';
import DeleteButton from '../../components/DeleteButton';
import CreateOverride from './CreateOverride';
import EditOverride from './EditOverride';
import { DELETE_CERTIFICATE_OVERRIDE_MUTATION } from '../../gql/mutations/DELETE_CERTIFICATE_OVERRIDE_MUTATION';

const EditCertificateForm = ( { certificate, saveCertificateCallback, 
    certificateOverrides, addCertificateCallback, ref } ) =>  {

    const [ formController ] = Form.useForm();
    const [ isSaving, setIsSaving ] = useState( false );
    
    const initialFormValues = {
        id: certificate?.id || '',
        name: '',
        description: '',
        brandId: '',
        certificateProfileId: '',
    };

    useImperativeHandle( ref, () => ( {
        submit() { 
            formController.submit(); 
        },
        setSaving( saving ) {
            setIsSaving( saving );     
        }
    } ) );

    const [ deleteCertificateOverride ] = useMutation( DELETE_CERTIFICATE_OVERRIDE_MUTATION );

    useEffect( () => {
        if ( certificate ) {
            formController.setFields( [ 
                { 
                    name: 'id', 
                    value: certificate.id
                },
                { 
                    name: 'name', 
                    value: certificate.name !== null ? certificate.name : ''
                },
                { 
                    name: 'description', 
                    value: certificate.description !== null ? certificate.description : ''
                }, 
                { 
                    name: 'brandId', 
                    value: certificate.brand?.id 
                },
                { 
                    name: 'certificateProfileId', 
                    value: certificate.certificateProfile?.id 
                }
            ] );
        }
    }, [ certificate ] );

    const columns = useMemo( () => {
        return [ 
            {
                key: 'name',
                dataIndex: 'name',
                title: 'Name',
                sorter: ( a, b ) => a.name.localeCompare( b.name )
            },
            {
                key: 'preview',
                dataIndex: 'preview',
                title: 'Preview',
                render: ( text ) => 
                    <Button
                        size="small"
                        onClick={ () => handlePreviewClick( text ) }
                    >
                        Preview
                    </Button>
            },
            {
                key: 'actions', 
                title: 'Actions',
                fixed: 'right', 
                width: '100px',
                render: ( i ) => 
                    <Space>
                        <EditOverride 
                            certificate={ i } 
                            addCertificateCallback={ addCertificateCallback } 
                        />
                        <DeleteButton
                            buttonType="link"
                            confirmMessage= { 
                                <React.Fragment>
                                    <p>Are you <i>sure</i> you want to permanently delete this? 
                                    This action cannot be undone.</p>
                                </React.Fragment>
                            }
                            danger
                            superSafe={ false }
                            icon={ <DeleteOutlined /> }
                            item={ i }   
                            onDelete={ ( i ) => handleDeleteClick( i ) }
                        >
                        </DeleteButton>
                    </Space>
            }
        ];
    }, [] );

    /*************************************
     *  ANCHOR Event Handlers
     *************************************/
 
    const handleFormSubmission = ( values ) => {
        if ( saveCertificateCallback ) {
            setIsSaving( true );
            saveCertificateCallback( values );
        }
    };

    const handlePreviewClick = ( text ) => {
        window.open( text, '_blank' );
    };
    
    const handleDeleteClick = ( item ) => {
        setIsSaving( true );

        deleteCertificateOverride( {
            variables: {
                input: { 
                    id: item.id
                }
            },
        } ).then( res => {
            setIsSaving( false );
            addCertificateCallback();
        } ).catch( err => {
            message.error( 'Error deleting certificate' );
            setIsSaving( false );
        } );
    };

    /*************************************
     *  ANCHOR Render
     *************************************/
 
    return (
        <Form
            form={ formController }
            name="cert-form"
            layout="vertical"
            initialValues={ initialFormValues }
            onFinish={ handleFormSubmission }
            disabled={ isSaving }
            scrollToFirstError
        >      
            <CreateOverride
                certificateId={ certificate.id } 
                addCertificateCallback={ addCertificateCallback } 
            />
             
            <Table 
                columns={ columns }
                dataSource={ certificateOverrides }
                rowKey='id'
            />
                
            { /* Name (hidden) */ }
            <Form.Item
                name="name"
                nostyle
                style={ { display: 'none' } }
            />

            { /* Description (hidden)  */ }
            <Form.Item
                name="description"
                nostyle
            />

            { /* Brands (hidden)  */ }
            {  flags.BRANDS  &&
            <Form.Item
                name='brandId'
                nostyle
                style={ { display: 'none' } }
            />
            }

            { /* Profiles (hidden) */ }
            <Form.Item
                name="certificateProfileId"
                nostyle
                style={ { display: 'none' } }
            />
            
            { /* id (hidden) */ }
            <Form.Item
                name="id"
                nostyle
                style={ { display: 'none' } }
            />
        </Form>   
    );
};

export default EditCertificateForm;


// Saving full form items for later
// { /* Name */ }
// <Form.Item
//     name="name"
//     label="Name"
//     maxLength= { 64 }
//     rules={ [
//         {
//             required: true,
//             message: 'This certificate required to have a name.',
//         },
//         {
//             max: 64,
//             type: 'string',
//             message: 'The name must be 64 characters or shorter.',
//         }
//     ] }
// >
//     <Input disabled />
// </Form.Item>

// { /* Description */ }
// <Form.Item
//     name="description"
//     label="Description"
//     maxLength= { 200 }
//     rules={ [
//         {
//             max: 200,
//             type: 'string',
//             message: 'Description must be 200 characters or shorter.',
//         }
//     ] }
// >
//     <Input.TextArea
//         disabled
//         rows={ 4 }
//     />
// </Form.Item>

// { /* Brands */ }
// {  flags.BRANDS  &&
// <Form.Item
//     name='brandId'
//     label='Select Brand'
//     rules={ [
//         { 
//             required: true,
//             message: 'Certificates are required to have a brand.' 
//         }
//     ] }
// >
//     <Select disabled>
//         { 
//             brands.map( i => 
//                 <Select.Option 
//                     key={ i.id } 
//                     value={ i.id }
//                 >
//                     { i.name }
//                 </Select.Option> 
//             ) 
//         }
//     </Select>
// </Form.Item>
// }

// { /* Profiles */ }
// <Form.Item
//     name="certificateProfileId"
//     label="Profile"
//     maxLength= { 64 }
//     rules={ [
//         {
//             required: true,
//             message: 'Certificates are required to have a profile.',
//         }
//     ] }
// >
//     <Select >
//         { 
//             profiles.map( i => 
//                 <Select.Option 
//                     key={ i.id } 
//                     value={ i.id }
//                 >
//                     { i.name }
//                 </Select.Option> 
//             ) 
//         }
//     </Select>
// </Form.Item>
